import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Collapse,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import LockIcon from '@mui/icons-material/Lock';
import { HomeIcon, TrainingIcon, ToolkitIcon, ReportsIcon } from '../icons/NavigationIcons';
import { useAuth } from '../../contexts/AuthContext';
import { courses, flows } from '../../services/api';

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
}

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2),
  backgroundColor: '#000000',
  color: 'white',
}));

interface StyledListItemProps {
  active?: boolean;
}

const StyledListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'active',
})<StyledListItemProps>(({ theme, active }) => ({
  borderRadius: '15px',
  marginBottom: theme.spacing(1),
  backgroundColor: active ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
  cursor: 'pointer',
  padding: theme.spacing(1.5, 2),
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
}));

interface SubMenuItemProps {
  active?: boolean;
}

const SubMenuItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'active',
})<SubMenuItemProps>(({ theme, active }) => ({
  paddingLeft: theme.spacing(4),
  borderRadius: '15px',
  marginBottom: theme.spacing(1),
  cursor: 'pointer',
  backgroundColor: active ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
}));

const navigationItems = [
  { text: 'Home', icon: HomeIcon, path: '/home' },
  { 
    text: 'Lifeplan', 
    icon: TrainingIcon, 
    path: '/training',
    hasFlows: true
  },
  { text: 'Toolkit', icon: ToolkitIcon, path: '/toolkit' },
  { text: 'Reports', icon: ReportsIcon, path: '/reports' },
];

export const MobileMenu: React.FC<MobileMenuProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [lifePlanOpen, setLifePlanOpen] = useState(false);
  const [lifePlanFlows, setLifePlanFlows] = useState<any[]>([]);
  const [flowStatus, setFlowStatus] = useState<Record<string, { completed: boolean, requirementsMet: boolean }>>({});

  useEffect(() => {
    const fetchLifePlanFlows = async () => {
      try {
        const flowsResponse = await courses.getFlows(4);
        console.log('[MobileMenu] Flows data:', flowsResponse.data);
        
        if (Array.isArray(flowsResponse.data)) {
          setLifePlanFlows(flowsResponse.data);
          
          const flowsData = flowsResponse.data;
          const statusMap: Record<string, { completed: boolean, requirementsMet: boolean }> = {};
          
          for (const flowItem of flowsData) {
            const flowDetails = flowItem.flow_details || {};
            const flowName = flowDetails.name || '';
            
            if (flowName) {
              try {
                const completionStatus = await flows.getCompletionStatus(flowName);
                const requirementsStatus = await flows.checkRequirementsMet(flowName);
                
                statusMap[flowName] = {
                  completed: !!completionStatus.completed_at,
                  requirementsMet: requirementsStatus.requirements_met
                };
              } catch (error) {
                console.error(`[MobileMenu] Error fetching status for flow ${flowName}:`, error);
                statusMap[flowName] = { completed: false, requirementsMet: true };
              }
            }
          }
          
          setFlowStatus(statusMap);
        } else {
          console.error('[MobileMenu] Unexpected data format:', flowsResponse.data);
        }
      } catch (error) {
        console.error('[MobileMenu] Error fetching lifeplan flows:', error);
      }
    };

    if (open) {
      fetchLifePlanFlows();
    }
  }, [open]);

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleLifePlanClick = () => {
    setLifePlanOpen(!lifePlanOpen);
  };

  const renderFlowIcon = (flowName: string) => {
    const status = flowStatus[flowName];
    
    if (!status) {
      return <CircleIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.3)' }} />;
    }
    
    if (status.completed) {
      return <CheckCircleIcon fontSize="small" color="primary" />;
    }
    
    if (!status.requirementsMet) {
      return <LockIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.3)' }} />;
    }
    
    return <CircleIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.6)' }} />;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '85%',
          maxWidth: '320px',
          backgroundColor: '#121212',
          color: 'white',
        },
      }}
    >
      <DrawerHeader>
        <Typography variant="h6">Menu</Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DrawerHeader>
      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
      <Box sx={{ p: 2 }}>
        <List>
          {navigationItems.map((item) => {
            const isActive = location.pathname === item.path;
            const isLifePlan = item.text === 'Lifeplan';
            
            return (
              <React.Fragment key={item.text}>
                {isLifePlan ? (
                  <>
                    <StyledListItem active={isActive} onClick={handleLifePlanClick}>
                      <ListItemIcon sx={{ minWidth: 36, color: isActive ? 'primary.main' : 'rgba(255, 255, 255, 0.7)' }}>
                        <item.icon />
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.text} 
                        sx={{ 
                          '& .MuiListItemText-primary': { 
                            color: isActive ? 'primary.main' : 'rgba(255, 255, 255, 0.9)' 
                          } 
                        }} 
                      />
                      {lifePlanOpen ? <ExpandLess /> : <ExpandMore />}
                    </StyledListItem>
                    <Collapse in={lifePlanOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {lifePlanFlows.map((flowItem) => {
                          const flowDetails = flowItem.flow_details || {};
                          const flowName = flowDetails.name || '';
                          const flowPath = `/flows/${flowName}`;
                          const isFlowActive = location.pathname === flowPath;
                          const status = flowStatus[flowName];
                          const isLocked = status && !status.requirementsMet;
                          
                          return (
                            <SubMenuItem 
                              key={flowName} 
                              active={isFlowActive}
                              onClick={() => {
                                if (!isLocked) {
                                  handleNavigation(flowPath);
                                }
                              }}
                              sx={{ opacity: isLocked ? 0.5 : 1 }}
                            >
                              <ListItemIcon sx={{ minWidth: 36, color: isFlowActive ? 'primary.main' : 'rgba(255, 255, 255, 0.7)' }}>
                                {renderFlowIcon(flowName)}
                              </ListItemIcon>
                              <ListItemText 
                                primary={flowDetails.name} 
                                sx={{ 
                                  '& .MuiListItemText-primary': { 
                                    color: isFlowActive ? 'primary.main' : 'rgba(255, 255, 255, 0.9)' 
                                  } 
                                }} 
                              />
                            </SubMenuItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </>
                ) : (
                  <StyledListItem 
                    active={isActive}
                    onClick={() => handleNavigation(item.path)}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: isActive ? 'primary.main' : 'rgba(255, 255, 255, 0.7)' }}>
                      <item.icon />
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text} 
                      sx={{ 
                        '& .MuiListItemText-primary': { 
                          color: isActive ? 'primary.main' : 'rgba(255, 255, 255, 0.9)' 
                        } 
                      }} 
                    />
                  </StyledListItem>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    </Drawer>
  );
};
